import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError, fmtDateTime } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Search, Sparkles, Archive, Undo2, Pencil } from "lucide-react";

const EMPTY = { trigger: "", title: "", body: "", field: "any", scope: "shared" };

export default function CannedTexts() {
  const [meta, setMeta] = useState(null);
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState({ q: "", field: "", scope: "", include_retired: "" });
  const [form, setForm] = useState({ ...EMPTY });
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    try {
      const p = { ...filters };
      Object.keys(p).forEach((k) => !p[k] && delete p[k]);
      const { data } = await api.get("/canned-texts", { params: p });
      setRows(data);
    } catch (e) { toast.error(apiError(e)); }
  }, [filters]);

  useEffect(() => { api.get("/canned-texts/meta").then((r) => setMeta(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const startNew = () => { setEditing(null); setForm({ ...EMPTY }); setOpen(true); };
  const startEdit = (row) => {
    setEditing(row);
    setForm({ trigger: row.trigger, title: row.title, body: row.body, field: row.field, scope: row.scope });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy("save");
    try {
      if (editing) await api.put(`/canned-texts/${editing.id}`, form);
      else await api.post("/canned-texts", form);
      toast.success(editing ? "Snippet updated" : `Snippet saved — type .${form.trigger.replace(/^\./, "")} then Tab`);
      setOpen(false);
      setForm({ ...EMPTY });
      setEditing(null);
      load();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(""); }
  };

  const toggleRetire = async (row) => {
    try {
      await api.post(`/canned-texts/${row.id}/${row.retired ? "restore" : "retire"}`);
      toast.success(row.retired ? "Snippet restored" : "Snippet retired");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div className="space-y-5" data-testid="canned-texts-page">
      <header className="flex flex-wrap items-start gap-3">
        <div className="min-w-0">
          <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">Canned Texts</h1>
          <p className="mt-1 max-w-3xl text-sm text-slate-600">
            Reusable normals and boilerplate for transcription. Type <span className="font-mono font-bold">.trigger</span>
            {" "}then Tab inside any report field to expand, or pick from the Canned text panel. Use
            {" "}<span className="font-mono font-bold">[[blank]]</span> for fill-ins — Tab jumps to the next one.
          </p>
        </div>
        <Button onClick={startNew} data-testid="new-canned-button" className="ml-auto h-11 bg-[#134e4a] hover:bg-[#0f3f3c]">
          <Plus size={16} className="mr-2" /> New snippet
        </Button>
      </header>

      <div className="flex flex-wrap items-center gap-2" data-testid="canned-filters">
        <div className="relative w-full sm:w-64">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={filters.q} onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
                 placeholder="Trigger, title or text" data-testid="canned-search" className="h-10 pl-9" />
        </div>
        <select value={filters.field} onChange={(e) => setFilters((f) => ({ ...f, field: e.target.value }))}
                data-testid="canned-field-filter" className="h-10 border border-slate-300 bg-white px-2 text-sm">
          <option value="">All fields</option>
          {(meta?.fields || []).map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
        </select>
        <select value={filters.scope} onChange={(e) => setFilters((f) => ({ ...f, scope: e.target.value }))}
                data-testid="canned-scope-filter" className="h-10 border border-slate-300 bg-white px-2 text-sm">
          <option value="">Shared + mine</option>
          <option value="shared">Shared only</option>
          <option value="private">Mine only</option>
        </select>
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input type="checkbox" checked={filters.include_retired === "true"} data-testid="canned-retired-toggle"
                 onChange={(e) => setFilters((f) => ({ ...f, include_retired: e.target.checked ? "true" : "" }))}
                 className="h-4 w-4 accent-[#134e4a]" />
          Show retired
        </label>
      </div>

      <section className="grid gap-3 lg:grid-cols-2" data-testid="canned-list">
        {rows.length === 0 && (
          <p className="border border-slate-200 px-3 py-10 text-center text-sm text-slate-500 lg:col-span-2"
             data-testid="canned-empty">
            No snippets match. Create one with “New snippet”.
          </p>
        )}
        {rows.map((s) => (
          <article key={s.id} className={`border p-3 ${s.retired ? "border-slate-200 bg-slate-50 opacity-70" : "border-slate-200 bg-white"}`}
                   data-testid={`canned-card-${s.trigger}`}>
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-mono text-sm font-bold text-[#134e4a]">.{s.trigger}</span>
              <h2 className="text-base font-semibold text-slate-900">{s.title}</h2>
              <span className="bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-slate-600">{s.field_label}</span>
              {s.scope === "private" && (
                <span className="bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-amber-900">private</span>
              )}
              {s.retired && <span className="bg-slate-700 px-1.5 py-0.5 text-[10px] font-bold uppercase text-white">retired</span>}
              <span className="ml-auto flex gap-1.5">
                <Button size="sm" variant="outline" onClick={() => startEdit(s)} data-testid={`canned-edit-${s.trigger}`}
                        className="h-8 border-slate-300"><Pencil size={13} /></Button>
                <Button size="sm" variant="outline" onClick={() => toggleRetire(s)} data-testid={`canned-retire-${s.trigger}`}
                        className="h-8 border-slate-300">{s.retired ? <Undo2 size={13} /> : <Archive size={13} />}</Button>
              </span>
            </div>
            <pre className="mt-2 max-h-40 overflow-y-auto whitespace-pre-wrap border-l-2 border-slate-200 pl-2 font-mono text-xs text-slate-700">
              {s.body}
            </pre>
            <div className="mt-2 flex flex-wrap gap-x-3 text-[11px] text-slate-500">
              <span className="inline-flex items-center gap-1"><Sparkles size={11} /> used {s.usage_count}×</span>
              {s.placeholders.length > 0 && <span>{s.placeholders.length} fill-in(s)</span>}
              <span>{s.scope === "private" ? `private to ${s.owner_name || "you"}` : `shared · added by ${s.created_by}`}</span>
              {s.last_used_at && <span>last used {fmtDateTime(s.last_used_at)}</span>}
            </div>
          </article>
        ))}
      </section>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto" data-testid="canned-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">{editing ? "Edit snippet" : "New snippet"}</DialogTitle>
            <DialogDescription>
              Wrap fill-ins in double square brackets, e.g. “measuring [[length]] cm”.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="grid gap-3 sm:grid-cols-2" data-testid="canned-form">
            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-600">Trigger</Label>
              <Input value={form.trigger} onChange={set("trigger")} data-testid="canned-trigger-input"
                     placeholder="nsk" className="mt-1 h-11 border-slate-300 font-mono" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-600">Title</Label>
              <Input value={form.title} onChange={set("title")} data-testid="canned-title-input"
                     placeholder="Normal skin excision — gross" className="mt-1 h-11 border-slate-300" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-600">Field</Label>
              <select value={form.field} onChange={set("field")} data-testid="canned-field-select"
                      className="mt-1 h-11 w-full rounded-md border border-slate-300 bg-white px-3 text-sm">
                {(meta?.fields || []).map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-600">Shared or private</Label>
              <select value={form.scope} onChange={set("scope")} data-testid="canned-scope-select"
                      className="mt-1 h-11 w-full rounded-md border border-slate-300 bg-white px-3 text-sm">
                <option value="shared">Shared with the department</option>
                <option value="private">Private to me</option>
              </select>
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs uppercase tracking-wide text-slate-600">Text</Label>
              <Textarea value={form.body} onChange={set("body")} data-testid="canned-body-input" rows={8}
                        placeholder="Received in formalin, labeled with the patient's name and “[[site]]”…"
                        className="mt-1 font-mono text-sm border-slate-300" />
            </div>
            <Button type="submit" disabled={busy === "save"} data-testid="canned-save"
                    className="h-11 sm:col-span-2 bg-[#134e4a] hover:bg-[#0f3f3c]">
              {busy === "save" ? "Saving…" : editing ? "Save changes" : "Create snippet"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
